import { useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  User,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Timer,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { logout, updateTokens, selectCurrentUser, selectAccessToken } from '../../store/authSlice'
import { useRefreshTokenMutation } from '../../services/authApi'
import { useGetAdminNavMenusQuery } from '../../services/menuApi'
import type { RootState } from '../../store'
import type { Menu as MenuType } from '../../types/menu'
import { MenuProvider } from '../../contexts/MenuContext'
import type { MenuItem } from '../../contexts/MenuContext'

// const ChatWidget = lazy(() => import('../chatbot/ChatWidget')) // CHATBOT DISABLED

interface AdminLayoutProps {
  children: ReactNode
}

/** 메뉴에 등록된 경로 집합 (exact match 강제 경로는 없음 - bestMatchPath 방식 사용) */

/** lnkg_addr가 절대 URL이거나 앞뒤 슬래시 형태가 다를 수 있으므로 정규화 */
function normalizePath(p: string): string {
  let path = p.trim()
  try {
    if (path.startsWith('http://') || path.startsWith('https://')) {
      path = new URL(path).pathname
    }
  } catch {
    // keep as-is
  }
  // 쿼리스트링 및 해시 제거
  path = path.split('?')[0].split('#')[0]
  if (!path.startsWith('/')) path = '/' + path
  // 루트(/)가 아닌 경우 후행 슬래시 제거
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1)
  return path || '/'
}

/**
 * t_menu 테이블에서 가져온 flat 목록을 트리 구조의 MenuItem[]로 변환
 */
function buildMenuTree(flatMenus: MenuType[]): MenuItem[] {
  // menu_no -> MenuItem 매핑
  const map = new Map<string, MenuItem & { menu_no: string; up_menu_no?: string }>()

  // 1차: 모든 항목을 MenuItem 로 변환
  for (const m of flatMenus) {
    map.set(m.menu_no, {
      id: m.menu_no,
      menu_no: m.menu_no,
      up_menu_no: m.up_menu_no,
      text: m.menu_nm,
      path: m.lnkg_addr || undefined,
      smap_use_yn: m.smap_use_yn,
      children: [],
    })
  }

  // 2차: 부모-자식 관계 연결 (parent가 응답에 없으면 orphan → 제외)
  const roots: MenuItem[] = []
  for (const item of map.values()) {
    if (item.up_menu_no && map.has(item.up_menu_no)) {
      map.get(item.up_menu_no)!.children!.push(item)
    } else if (!item.up_menu_no) {
      // 최상위 메뉴 (up_menu_no가 null)
      roots.push(item)
    }
    // up_menu_no가 있지만 map에 없는 경우 = parent가 비활성 → 제외
  }

  // 3차: 빈 children 배열 정리 (leaf 노드는 children 제거)
  function cleanChildren(items: MenuItem[]) {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        cleanChildren(item.children)
      } else {
        delete item.children
      }
    }
  }
  cleanChildren(roots)

  return roots
}

function parseJwtExp(token: string | null): number | null {
  if (!token) return null
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.exp ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [megaMenuOpen, setMegaMenuOpen] = useState(false)
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null)
  const [showExpireWarning, setShowExpireWarning] = useState(false)
  // 사용자가 "취소"를 누른 경우 동일 세션에서는 다시 띄우지 않도록 추적
  const expireWarningDismissedRef = useRef(false)
  // 직전 tick 의 남은 초. 경고를 'edge-trigger'(>300 → ≤300 최초 진입) 로만 띄우기 위함.
  // 연장 직후 새 토큰/구 타이머가 즉시 재팝업하는 문제 방지.
  const prevDiffRef = useRef<number>(Number.POSITIVE_INFINITY)
  const menuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const user = useSelector(selectCurrentUser)
  const accessToken = useSelector(selectAccessToken)
  const refreshToken = useSelector((state: RootState) => state.auth.refreshToken)
  const [refreshTokenMutation] = useRefreshTokenMutation()

  // t_menu 테이블에서 동적 메뉴 조회
  const { data: navMenuData } = useGetAdminNavMenusQuery()
  const menuItems = useMemo<MenuItem[]>(() => {
    if (!navMenuData?.data) return []
    return buildMenuTree(navMenuData.data)
  }, [navMenuData])

  const currentPath = normalizePath(location.pathname)

  /** 메뉴 트리에서 모든 경로를 추출 (긴 것부터 정렬) */
  const allMenuPaths = useMemo(() => {
    const paths: string[] = []
    const collect = (items: MenuItem[]) => {
      for (const item of items) {
        if (item.path) paths.push(normalizePath(item.path))
        if (item.children) collect(item.children)
      }
    }
    collect(menuItems)
    return paths.sort((a, b) => b.length - a.length)
  }, [menuItems])

  /**
   * 현재 경로와 prefix 매칭되는 가장 긴 메뉴 경로
   * - /member/supplier 접속 시 → /member/supplier (O), /member (X)
   * - /member/MB000000003 접속 시 → /member (O) — 더 긴 매칭 없음
   */
  const bestMatchPath = useMemo(() => {
    for (const p of allMenuPaths) {
      if (currentPath === p || currentPath.startsWith(p + '/')) return p
    }
    return null
  }, [allMenuPaths, currentPath])

  // 세션 타이머
  useEffect(() => {
    const expMs = parseJwtExp(accessToken)
    if (!expMs) {
      setRemainingSeconds(null)
      setShowExpireWarning(false)
      return
    }

    const initialDiff = Math.max(0, Math.floor((expMs - Date.now()) / 1000))
    // 새 토큰이 충분히(>300s) 남았을 때만 경고 dismiss 상태를 리셋한다.
    // 연장 직후 새 토큰이 여전히 ≤300s 인 경우(짧은 TTL 등) 즉시 재팝업되는 것을 방지.
    if (initialDiff > 300) {
      expireWarningDismissedRef.current = false
    }
    // prevDiffRef 는 effect 간 carry 한다(여기서 initialDiff 로 덮어쓰지 않음).
    //  - 최초 마운트: 기본값 Infinity → 토큰이 이미 ≤300s 여도 1회 경고 노출(기존 동작 유지).
    //  - 연장 직후 재실행: 직전 토큰의 마지막 diff(≤300)가 그대로 carry → 새 토큰이 길든 짧든
    //    첫 tick 에서 edge(>300→≤300) 가 성립하지 않아 즉시 재팝업되지 않음.

    const tick = () => {
      const diff = Math.max(0, Math.floor((expMs - Date.now()) / 1000))
      setRemainingSeconds(diff)
      const prev = prevDiffRef.current
      prevDiffRef.current = diff
      // 남은 시간이 '>300 → ≤300' 으로 처음 진입할 때만 경고 (edge-trigger).
      if (
        prev > 300 &&
        diff > 0 &&
        diff <= 300 &&
        !expireWarningDismissedRef.current
      ) {
        setShowExpireWarning(true)
      }
      if (diff <= 0) {
        setShowExpireWarning(false)
        dispatch(logout())
        navigate('/login')
      }
    }

    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [accessToken, dispatch, navigate])

  const handleExtendSession = useCallback(async () => {
    if (!refreshToken) return
    try {
      const result = await refreshTokenMutation({ refreshToken }).unwrap()
      if (result.data) {
        dispatch(updateTokens({
          accessToken: result.data.accessToken,
          refreshToken: result.data.refreshToken,
        }))
      }
    } catch {
      dispatch(logout())
      navigate('/login')
    }
  }, [refreshToken, refreshTokenMutation, dispatch, navigate])

  const handleExtendFromWarning = useCallback(async () => {
    // 연장 버튼 선택 시: 토큰 갱신 네트워크 왕복 동안 구 타이머가 경고를 재팝업하지 않도록
    // 먼저 dismiss 처리한다. 새 토큰이 >300s 로 갱신되면 타이머 effect 에서 자동 재-arm 된다.
    expireWarningDismissedRef.current = true
    setShowExpireWarning(false)
    await handleExtendSession()
  }, [handleExtendSession])

  const handleDismissWarning = useCallback(() => {
    expireWarningDismissedRef.current = true
    setShowExpireWarning(false)
  }, [])

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  const handleLogout = () => {
    dispatch(logout())
    navigate('/login')
  }

  const handleLogoClick = useCallback(() => {
    navigate('/dashboard')
  }, [navigate])

  const handleMenuEnter = useCallback((menuText: string) => {
    if (menuCloseTimer.current) {
      clearTimeout(menuCloseTimer.current)
      menuCloseTimer.current = null
    }
    setActiveMenu(menuText)
  }, [])

  const handleMenuLeave = useCallback(() => {
    menuCloseTimer.current = setTimeout(() => {
      setActiveMenu(null)
      menuCloseTimer.current = null
    }, 120)
  }, [])

  const handleMenuClick = (path?: string) => {
    if (path) {
      navigate(normalizePath(path))
      setActiveMenu(null)
      setMegaMenuOpen(false)
      if (menuCloseTimer.current) {
        clearTimeout(menuCloseTimer.current)
        menuCloseTimer.current = null
      }
    }
  }

  /** 최상위 메뉴 클릭 시 첫 번째 leaf 자식 경로 반환 (없으면 자기 경로) */
  const getFirstLeafPath = useCallback((item: MenuItem): string | undefined => {
    if (item.children && item.children.length > 0) {
      return getFirstLeafPath(item.children[0])
    }
    return item.path
  }, [])

  const handleTopMenuClick = (item: MenuItem) => {
    const target = getFirstLeafPath(item)
    if (target) handleMenuClick(target)
  }

  /** 현재 경로와 메뉴 경로를 비교 - bestMatchPath만 true */
  const isPathMatch = useCallback((itemPath: string): boolean => {
    return normalizePath(itemPath) === bestMatchPath
  }, [bestMatchPath])

  /** 드롭다운 아이템용: 재귀적으로 현재 경로와 일치하는 항목 탐색 */
  const isMenuSelected = (item: MenuItem): boolean => {
    if (item.path) return isPathMatch(item.path)
    if (item.children) return item.children.some((child) => isMenuSelected(child))
    return false
  }

  /**
   * 최상위 nav 아이템의 active 여부
   * bestMatchPath와 일치하는 경로를 가진 최상위 메뉴 ID
   */
  const activeTopLevelId = useMemo(() => {
    if (!bestMatchPath) return null
    const traverse = (item: MenuItem, rootId: string): string | null => {
      if (item.path && normalizePath(item.path) === bestMatchPath) return rootId
      if (item.children) {
        for (const child of item.children) {
          const found = traverse(child, rootId)
          if (found) return found
        }
      }
      return null
    }
    for (const item of menuItems) {
      const found = traverse(item, item.id)
      if (found) return found
    }
    return null
  }, [menuItems, bestMatchPath])

  // 드롭다운 메뉴 아이템 렌더링
  const renderDropdownItem = (item: MenuItem) => {
    const hasChildren = item.children && item.children.length > 0
    const isSelected = isMenuSelected(item)

    if (hasChildren) {
      return (
        <div key={item.id} className="mb-1">
          <div className="text-xs font-medium text-gray-500 px-2 py-1 uppercase tracking-wide">
            {item.text}
          </div>
          <div className="ml-2">
            {item.children!.map((child) => (
              <button
                key={child.id}
                role="menuitem"
                onClick={() => handleMenuClick(child.path)}
                className={cn(
                  'flex items-center w-full text-left px-2 py-1.5 text-sm rounded transition-colors',
                  isMenuSelected(child)
                    ? 'text-primary bg-primary/10 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                <span className="text-primary mr-1.5" aria-hidden="true">•</span>
                {child.text}
              </button>
            ))}
          </div>
        </div>
      )
    }

    return (
      <button
        key={item.id}
        role="menuitem"
        onClick={() => handleMenuClick(item.path)}
        className={cn(
          'flex items-center w-full text-left px-2 py-1.5 text-sm rounded transition-colors',
          isSelected
            ? 'text-primary bg-primary/10 font-medium'
            : 'text-gray-700 hover:bg-gray-100'
        )}
      >
        {item.text}
      </button>
    )
  }

  // 메가메뉴용 아이템 렌더링
  // SMAP_USE_YN === 'N' 인 메뉴는 사이트맵에서 제외한다.
  const renderMegaMenuItem = (item: MenuItem) => {
    if (item.smap_use_yn === 'N') return null

    const visibleChildren = (item.children || []).filter(
      (c) => c.smap_use_yn !== 'N',
    )
    const hasChildren = visibleChildren.length > 0

    if (hasChildren) {
      return (
        <div key={item.id}>
          <div className="text-sm text-gray-500 mb-1">{item.text}</div>
          {visibleChildren.map((child) => (
            <button
              key={child.id}
              onClick={() => handleMenuClick(child.path)}
              className={cn(
                'flex items-center w-full text-left py-1 text-sm transition-colors',
                isMenuSelected(child)
                  ? 'text-primary font-medium'
                  : 'text-gray-700 hover:text-primary'
              )}
            >
              <span className="text-primary mr-1.5">•</span>
              {child.text}
            </button>
          ))}
        </div>
      )
    }

    return (
      <button
        key={item.id}
        onClick={() => handleMenuClick(item.path)}
        className={cn(
          'flex items-center w-full text-left py-1 text-sm transition-colors',
          isMenuSelected(item)
            ? 'text-primary font-medium'
            : 'text-gray-700 hover:text-primary'
        )}
      >
        {item.text}
      </button>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Skip Navigation */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-0 focus:left-0 focus:z-[9999] focus:bg-primary focus:text-white focus:px-4 focus:py-2"
      >
        본문 바로가기
      </a>

      {/* Top Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-50">
        {/* Main Header */}
        <div className="h-14 px-6 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <button
              onClick={handleLogoClick}
              className="flex items-center"
            >
              <img src="/images/logo.png" alt="삼척 돌봄 E-음" className="h-9 object-contain" />
              <span className="ml-2 text-sm font-medium text-gray-500"></span>
            </button>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700 hidden md:inline">
              {user?.mbrNm || '관리자'}
            </span>

            <div className="h-4 w-px bg-gray-300 hidden md:block" />

            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 h-8 text-gray-700 hover:text-primary"
              onClick={() => navigate('/profile')}
            >
              <User className="h-4 w-4" />
              내 정보
            </Button>

            <div className="h-4 w-px bg-gray-300" />

            {remainingSeconds !== null && (
              <>
                <div className="flex items-center gap-1.5" role="timer" aria-label="세션 남은 시간">
                  <Timer className="h-3.5 w-3.5 text-gray-500" aria-hidden="true" />
                  <span className={cn(
                    'text-sm font-mono tabular-nums',
                    remainingSeconds <= 300 ? 'text-red-600 font-semibold' : 'text-gray-600'
                  )}>
                    {formatTime(remainingSeconds)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={handleExtendSession}
                    aria-label="세션 연장"
                  >
                    연장
                  </Button>
                </div>
                <div className="h-4 w-px bg-gray-300" />
              </>
            )}

            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 h-8 text-gray-700 hover:text-destructive"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4" />
              로그아웃
            </Button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="bg-white border-t" aria-label="주 메뉴">
          <div className="flex items-center px-6">
            {menuItems.map((item) => (
              <div
                key={item.id}
                className="relative"
                onMouseEnter={() => handleMenuEnter(item.text)}
                onMouseLeave={handleMenuLeave}
                onFocus={() => item.children && handleMenuEnter(item.text)}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    handleMenuLeave()
                  }
                }}
              >
                <button
                  className={cn(
                    'flex items-center gap-1 px-4 py-3 text-sm font-medium transition-colors duration-150 border-b-2 -mb-px',
                    activeMenu === item.text || activeTopLevelId === item.id
                      ? 'text-primary border-primary'
                      : 'text-gray-700 border-transparent hover:text-primary'
                  )}
                  {...(item.children ? {
                    'aria-haspopup': 'true' as const,
                    'aria-expanded': activeMenu === item.text,
                  } : {})}
                  onClick={() => handleTopMenuClick(item)}
                  onKeyDown={(e) => {
                    if (item.children && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
                      e.preventDefault()
                      handleMenuEnter(item.text)
                    }
                    if (e.key === 'Escape') {
                      handleMenuLeave()
                    }
                  }}
                >
                  {item.text}
                  {item.children && (
                    <ChevronDown className={cn(
                      'h-3.5 w-3.5 transition-transform duration-200',
                      activeMenu === item.text && 'rotate-180'
                    )} aria-hidden="true" />
                  )}
                </button>

                {/* Dropdown Menu */}
                {item.children && (
                  <div
                    role="menu"
                    aria-label={`${item.text} 하위 메뉴`}
                    className={cn(
                      'absolute left-0 top-full bg-white border shadow-lg rounded-b-lg min-w-[220px] z-50 transition-all duration-150',
                      activeMenu === item.text
                        ? 'opacity-100 translate-y-0 visible'
                        : 'opacity-0 -translate-y-1 invisible pointer-events-none'
                    )}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        handleMenuLeave()
                      }
                    }}
                  >
                    <div className="py-2 px-1">
                      {item.children.map((child) => renderDropdownItem(child))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* 전체메뉴 버튼 */}
            <div className="ml-auto">
              <button
                onClick={() => setMegaMenuOpen(!megaMenuOpen)}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors rounded',
                  megaMenuOpen
                    ? 'bg-gray-100 text-primary'
                    : 'text-gray-600 hover:bg-gray-50'
                )}
                aria-expanded={megaMenuOpen}
                aria-label={megaMenuOpen ? '전체 메뉴 닫기' : '전체 메뉴 열기'}
              >
                {megaMenuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
              </button>
            </div>
          </div>
        </nav>

        {/* Mega Menu Overlay */}
        {megaMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setMegaMenuOpen(false)}
              aria-hidden="true"
            />
            <div className="absolute left-0 right-0 top-full bg-white border-t shadow-xl z-50">
              <div className="max-w-7xl mx-auto p-6">
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => setMegaMenuOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    aria-label="전체 메뉴 닫기"
                  >
                    <X className="h-6 w-6 text-gray-500" aria-hidden="true" />
                  </button>
                </div>
                <div className="grid grid-cols-8 gap-6">
                  {menuItems
                    .filter((category) => category.smap_use_yn !== 'N')
                    .map((category) => (
                      <div key={category.id} className="min-w-0">
                        <button
                          type="button"
                          onClick={() => handleTopMenuClick(category)}
                          className="block w-full text-left font-bold text-gray-900 mb-3 pb-2 border-b-2 border-primary hover:text-primary transition-colors"
                        >
                          {category.text}
                        </button>
                        <div className="space-y-1">
                          {category.children
                            ?.filter((item) => item.smap_use_yn !== 'N')
                            .map((item) => renderMegaMenuItem(item))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </>
        )}
      </header>

      {/* Page content */}
      <main id="main-content" className="flex-1 p-6">
        <MenuProvider menus={menuItems}>
          {children}
        </MenuProvider>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-gray-200 text-sm mt-auto">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center gap-x-8 gap-y-4">
          {/* 로고 */}
          <div className="shrink-0">
            <img
              src="/images/logo.png"
              alt="삼척 돌봄e음"
              className="h-10 object-contain brightness-0 invert"
            />
          </div>

          {/* 사업단 정보 · 카피라이트 */}
          <div className="flex-1 min-w-0 space-y-1">
            <p className="text-gray-300">
              (25913) 강원특별자치도 삼척시 중앙로 346 EDUPIA삼척교육발전사업단
            </p>
            <p className="text-gray-400 text-xs">
              Copyright 2025 ⓒ EDUPIA삼척교육발전사업단. All rights reserved.
            </p>
          </div>

          {/* 연락처 */}
          <div className="shrink-0 space-y-1 text-right">
            <p className="text-gray-300">대표전화 : 033-570-6841</p>
            {/* <p className="text-gray-300"> */}
            {/*   시스템 이용 문의 : <span className="text-red-400">추후 기재</span> */}
            {/* </p> */}
          </div>
        </div>
      </footer>

      {/* CHATBOT DISABLED
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>
      */}

      {/* 세션 만료 5분 전 경고. 외부 클릭/ESC로 닫히지 않도록 onOpenChange는 무시. */}
      <AlertDialog open={showExpireWarning}>
        <AlertDialogContent
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>세션 만료 안내</AlertDialogTitle>
            <AlertDialogDescription>
              {remainingSeconds !== null && remainingSeconds > 0 ? (
                <>
                  로그인 세션이 <strong>{formatTime(remainingSeconds)}</strong> 후 만료됩니다.
                  <br />
                  세션을 연장하시겠습니까?
                </>
              ) : (
                '세션이 곧 만료됩니다.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDismissWarning}>취소</AlertDialogCancel>
            <AlertDialogAction onClick={handleExtendFromWarning}>연장</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
